<template>
    <div class="profile-card">
        <div class="card-header">
            <h2>Security</h2>
            <button
                v-if="!editing"
                @click="startEditing"
                class="edit-btn"
            >
                Change Password
            </button>
        </div>
        <div class="card-content">
            <div v-if="!editing" class="info-row">
                <label>Password</label>
                <span class="info-value">••••••••••••</span>
            </div>

            <div v-else class="password-form">
                <!-- Current Password -->
                <div class="info-row">
                    <label>Current Password</label>
                    <div class="password-input-wrapper">
                        <input
                            v-model="passwordData.currentPassword"
                            :type="showCurrentPassword ? 'text' : 'password'"
                            class="input-field"
                            placeholder="Enter current password"
                        />
                        <button
                            type="button"
                            @click="showCurrentPassword = !showCurrentPassword"
                            class="toggle-password"
                        >
                            <component :is="showCurrentPassword ? Eye : EyeOff"/>
                        </button>
                    </div>
                </div>

                <!-- New Password -->
                <div class="info-row">
                    <label>New Password</label>
                    <div class="password-input-wrapper">
                        <input
                            v-model="passwordData.newPassword"
                            :type="showNewPassword ? 'text' : 'password'"
                            class="input-field"
                            placeholder="Enter new password"
                        />
                        <button
                            type="button"
                            @click="showNewPassword = !showNewPassword"
                            class="toggle-password"
                        >
                            <component :is="showNewPassword ? Eye : EyeOff"/>
                        </button>
                    </div>
                    <div v-if="passwordData.newPassword" class="password-strength">
                        <div class="strength-bar">
                            <div
                                class="strength-fill"
                                :class="`strength-${passwordStrength.level}`"
                                :style="{ width: `${passwordStrength.percentage}%` }"
                            ></div>
                        </div>
                        <span class="strength-text">{{ passwordStrength.text }}</span>
                    </div>
                </div>

                <!-- Confirm Password -->
                <div class="info-row">
                    <label>Confirm New Password</label>
                    <div class="password-input-wrapper">
                        <input
                            v-model="passwordData.confirmPassword"
                            :type="showConfirmPassword ? 'text' : 'password'"
                            class="input-field"
                            placeholder="Confirm new password"
                        />
                        <button
                            type="button"
                            @click="showConfirmPassword = !showConfirmPassword"
                            class="toggle-password"
                        >
                            <component :is="showConfirmPassword ? Eye : EyeOff"/>
                        </button>
                    </div>
                    <span
                        v-if="passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword"
                        class="error-text"
                    >
                        Passwords do not match
                    </span>
                </div>

                <!-- Action Buttons -->
                <div class="card-actions">
                    <button @click="cancelEdit" class="cancel-btn">
                        Cancel
                    </button>
                    <button
                        @click="changePassword"
                        class="save-btn"
                        :disabled="!canChangePassword || saving"
                    >
                        {{ saving ? 'Updating...' : 'Update Password' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { Eye, EyeOff } from 'lucide-vue-next';

export default {
    name: 'SecurityCard',
    props: {
        saving: {
            type: Boolean,
            default: false
        }
    },
    emits: ['change-password', 'cancel'],
    setup(props, { emit }) {
        const editing = ref(false);

        const passwordData = ref({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });

        const showCurrentPassword = ref(false);
        const showNewPassword = ref(false);
        const showConfirmPassword = ref(false);

        const passwordStrength = computed(() => {
            const password = passwordData.value.newPassword;
            if (!password) return { level: 'none', percentage: 0, text: '' };

            let strength = 0;
            if (password.length >= 10) strength += 25;
            if (password.length >= 12) strength += 15;
            if (/[a-z]/.test(password)) strength += 15;
            if (/[A-Z]/.test(password)) strength += 15;
            if (/[0-9]/.test(password)) strength += 15;
            if (/[^a-zA-Z0-9]/.test(password)) strength += 15;

            let level = 'weak';
            let text = 'Weak';
            if (strength >= 75) {
                level = 'strong';
                text = 'Strong';
            } else if (strength >= 50) {
                level = 'medium';
                text = 'Medium';
            }

            return { level, percentage: strength, text };
        });

        const canChangePassword = computed(() => {
            return (
                passwordData.value.currentPassword &&
                passwordData.value.newPassword &&
                passwordData.value.confirmPassword &&
                passwordData.value.newPassword === passwordData.value.confirmPassword &&
                passwordData.value.newPassword.length >= 10 &&
                /[A-Z]/.test(passwordData.value.newPassword) &&
                /[a-z]/.test(passwordData.value.newPassword) &&
                /[0-9]/.test(passwordData.value.newPassword)
            );
        });

        // Watch for saving to become false (save completed successfully)
        watch(() => props.saving, (newVal, oldVal) => {
            if (oldVal === true && newVal === false) {
                resetForm();
            }
        });

        const startEditing = () => {
            editing.value = true;
        };

        const resetForm = () => {
            passwordData.value = {
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            };
            editing.value = false;
            showCurrentPassword.value = false;
            showNewPassword.value = false;
            showConfirmPassword.value = false;
        };

        const cancelEdit = () => {
            resetForm();
            emit('cancel');
        };

        const changePassword = () => {
            emit('change-password', {
                currentPassword: passwordData.value.currentPassword,
                newPassword: passwordData.value.newPassword
            });
        };

        return {
            editing,
            passwordData,
            showCurrentPassword,
            showNewPassword,
            showConfirmPassword,
            passwordStrength,
            canChangePassword,
            startEditing,
            cancelEdit,
            changePassword,
            Eye,
            EyeOff
        };
    }
};
</script>

<style scoped>
.profile-card {
    background: rgba(30, 30, 30, 0.4);
    border: 1px solid rgba(var(--accent-rgb), 0.6);
    overflow: hidden;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--accent);
}

.card-header h2 {
    margin: 0;
    color: var(--accent);
    font-size: 24px;
}

.edit-btn {
    padding: 8px 20px;
    background: transparent;
    color: var(--accent);
    border: 1px solid var(--accent);
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
}

.edit-btn:hover {
    background: var(--accent);
    transform: translateY(-1px);
    color: var(--bg-primary);
}

.card-content {
    padding: 30px;
    background-color: var(--bg-primary);
}

.info-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 25px;
}

.info-row:last-child {
    margin-bottom: 0;
}

.info-row label {
    color: var(--accent);
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1.2px;
}

.info-value {
    color: var(--text-primary);
    font-size: 18px;
    padding: 12px 0;
}

.input-field {
    width: 100%;
    padding: 12px 16px;
    background: var(--bg-secondary);
    border: 1px solid var(--accent);
    color: var(--text-primary);
    font-size: 16px;
    transition: all 0.2s;
}

.input-field:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--bg-secondary);
}

.input-field::placeholder {
    color: rgba(255, 246, 227, 0.4);
}

/* Password Input */
.password-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.password-input-wrapper .input-field {
    padding-right: 50px;
}

.toggle-password {
    position: absolute;
    right: 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 20px;
    opacity: 0.6;
    transition: opacity 0.2s;
    color: rgba(255, 246, 227, 0.7);
    transform: translateY(3px);
}

.toggle-password:hover {
    opacity: 1;
}

.password-strength {
    margin-top: 8px;
}

.strength-bar {
    height: 4px;
    background: var(--bg-secondary);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 6px;
}

.strength-fill {
    height: 100%;
    transition: width 0.3s, background-color 0.3s;
}

.strength-weak {
    background: #ff4444;
}

.strength-medium {
    background: #ffaa00;
}

.strength-strong {
    background: #44ff44;
}

.strength-text {
    font-size: 12px;
    color: var(--text-primary);
    opacity: 0.8;
}

.error-text {
    color: #ff4444;
    font-size: 12px;
    margin-top: 4px;
}

.card-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid var(--accent);
}

.cancel-btn {
    padding: 12px 24px;
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--accent);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
}

.cancel-btn:hover {
    background: var(--bg-secondary);
}

.save-btn {
    padding: 12px 24px;
    background: var(--accent);
    color: var(--bg-primary);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
    font-weight: 600;
}

.save-btn:hover:not(:disabled) {
    background: var(--text-primary);
    transform: translateY(-2px);
}

.save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .card-header {
        padding: 15px 20px;
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .card-content {
        padding: 20px;
    }

    .card-actions {
        flex-direction: column;
    }

    .cancel-btn,
    .save-btn {
        width: 100%;
    }
}
</style>
